// app/stores/notificationStore.ts
import { makeAutoObservable, runInAction } from "mobx";

import { store } from "./store";
import { NotificationChannel, NotificationEventType, NotificationPreferenceDto, NotificationPreferencesGroup, UserNotificationPreference } from "../models/NotificationPreference";
import { agent } from "../api/agent";

export default class NotificationStore {
    userPreferences: UserNotificationPreference[] = [];
    channels: NotificationChannel[] = [];
    eventTypes: NotificationEventType[] = [];
    loading = false;
    loadingInitial = false;

    constructor() {
        makeAutoObservable(this);
    }

    get groupedPreferences(): NotificationPreferencesGroup[] {
        const categories = Array.from(new Set(this.eventTypes.map(et => et.category)));
        
        return categories.map(category => {
            const categoryEventTypes = this.eventTypes.filter(et => et.category === category);
            const preferencesMap = new Map<string, boolean>();
    
            categoryEventTypes.forEach(eventType => {
                this.channels.forEach(channel => {
                    const key = `${eventType.id}-${channel.id}`;
                    const preference = this.userPreferences.find(
                        p => p.notificationEventTypeId === eventType.id && 
                             p.notificationChannelId === channel.id
                    );
                    preferencesMap.set(key, preference?.isEnabled ?? true);
                });
            });
    
            return {
                category,
                eventTypes: categoryEventTypes,
                channels: this.channels.filter(c => c.isActive),
                preferences: preferencesMap
            };
        });
    }

    loadPreferences = async () => {
        this.loadingInitial = true;
        try {
            const [preferences, channels, eventTypes] = await Promise.all([
                agent.Notifications.getUserPreferences(),
                agent.Notifications.getChannels(),
                agent.Notifications.getEventTypes()
            ]);

            runInAction(() => {
                this.userPreferences = preferences;
                this.channels = channels;
                this.eventTypes = eventTypes;
                this.loadingInitial = false;
            });
        } catch (error) {
            console.error('Error loading notification preferences:', error);
            runInAction(() => {
                this.loadingInitial = false;
            });
        }
    };

    updatePreference = async (eventTypeId: string, channelId: string, isEnabled: boolean) => {
        this.loading = true;
        try {
            const dto: NotificationPreferenceDto = {
                notificationEventTypeId: eventTypeId,
                notificationChannelId: channelId,
                isEnabled
            };

            await agent.Notifications.updatePreference(dto);

            runInAction(() => {
                const existingIndex = this.userPreferences.findIndex(
                    p => p.notificationEventTypeId === eventTypeId && 
                         p.notificationChannelId === channelId
                );

                if (existingIndex >= 0) {
                    this.userPreferences[existingIndex].isEnabled = isEnabled;
                } else {
                    const eventType = this.eventTypes.find(et => et.id === eventTypeId);
                    const channel = this.channels.find(c => c.id === channelId);
                    
                    if (eventType && channel) {
                        this.userPreferences.push({
                            id: '', // Will be set by backend
                            userId: store.userStore.user!.userID,
                            notificationEventTypeId: eventTypeId,
                            notificationChannelId: channelId,
                            isEnabled,
                            eventType,
                            channel
                        });
                    }
                }
                this.loading = false;
            });
        } catch (error) {
            console.error('Error updating notification preference:', error);
            runInAction(() => {
                this.loading = false;
            });
            throw error;
        }
    };

    toggleAllForCategory = async (category: string, isEnabled: boolean) => {
        this.loading = true;
        try {
            const categoryEventTypes = this.eventTypes.filter(et => et.category === category);
            const activeChannels = this.channels.filter(c => c.isActive);
            
            const preferences: NotificationPreferenceDto[] = [];
            categoryEventTypes.forEach(eventType => {
                activeChannels.forEach(channel => {
                    preferences.push({
                        notificationEventTypeId: eventType.id,
                        notificationChannelId: channel.id,
                        isEnabled
                    });
                });
            });

            await agent.Notifications.updateMultiplePreferences(preferences);

            runInAction(() => {
                preferences.forEach(pref => {
                    const existingIndex = this.userPreferences.findIndex(
                        p => p.notificationEventTypeId === pref.notificationEventTypeId && 
                             p.notificationChannelId === pref.notificationChannelId
                    );

                    if (existingIndex >= 0) {
                        this.userPreferences[existingIndex].isEnabled = isEnabled;
                    }
                });
                this.loading = false;
            });
        } catch (error) {
            console.error('Error toggling category preferences:', error);
            runInAction(() => {
                this.loading = false;
            });
            throw error;
        }
    };
}